/**
 * Spinner primitive.
 *
 * Accessible loading indicator. Pure SVG so animation works without a
 * stylesheet, and values resolve from tokens (CP5).
 *
 * Size prop maps to token-backed dimensions. Color follows
 * `currentColor` so callers can theme it by setting `color` on the parent.
 *
 * Legacy equivalent: `.spinner` class (line 2256 in App.css) + a handful
 * of inline `<div className="spinner" />` usages.
 */

import type { CSSProperties } from 'react';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
  size?: SpinnerSize;
  /** Accessible label for screen readers. Defaults to "Loading". */
  label?: string;
  /** Override color (must be a token-backed value — e.g. var(--color-accent-primary)). */
  color?: string;
  className?: string;
  /** Override the test id (defaults to "spinner") so callers can target a specific instance. */
  'data-testid'?: string;
}

const sizeRem: Record<SpinnerSize, string> = {
  sm: '1.25rem',
  md: '1.75rem',
  lg: '2.25rem',
};

export function Spinner({
  size = 'md',
  label = 'Loading',
  color,
  className,
  'data-testid': testId = 'spinner',
}: SpinnerProps): JSX.Element {
  const dimension = sizeRem[size];
  const style: CSSProperties = {
    display: 'inline-block',
    width: dimension,
    height: dimension,
    color: color ?? 'var(--color-accent-primary)',
  };

  // The spin animation + its reduced-motion cap live in `responsive.css` keyed
  // off this class (@keyframes and @media can't be expressed inline, same as the
  // sheet animations). The `role="status"` + `aria-label` is the static, motion-
  // independent progress cue, so capping the spin under reduce never loses meaning.
  const cls = className ? `shopista-spinner ${className}` : 'shopista-spinner';

  return (
    <>
      <span
        role="status"
        aria-live="polite"
        aria-label={label}
        data-size={size}
        data-testid={testId}
        className={cls}
        style={style}
      >
        <svg
          viewBox="0 0 24 24"
          width="100%"
          height="100%"
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          aria-hidden="true"
        >
          {/* Faint full-circle track + a ~270° moving arc. The wide sweep and
              bold stroke keep the spin unmistakable even at sm, including the
              near-white variant on the accent-purple payment buttons. */}
          <circle cx="12" cy="12" r="9" opacity="0.3" />
          <path d="M12 3 a9 9 0 1 1 -9 9" />
        </svg>
      </span>
    </>
  );
}
